import PropTypes from 'prop-types';
import {
  IonInput,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import {
  getEffectiveProfileFieldType,
  PROFILE_FIELD_TYPES,
  sanitizeSingleSelectValue,
  sanitizeMultiSelectValue,
  getProfileFieldOptionNames,
  dedupeProfileFieldOptions,
} from './constants';

const getFieldLabel = (name, isRequired) =>
  isRequired ? `${name} *` : name;

const ERROR_COLOR = 'var(--ion-color-danger, #eb445a)';

const fieldMessageStyle = (isError) => ({
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  fontSize: '13px',
  lineHeight: '1.35',
  padding: '6px 16px 10px',
  margin: 0,
  color: isError ? ERROR_COLOR : 'var(--ion-color-medium, #666)',
});

/**
 * Ionic errorText/helper slots are unreliable inside stacked IonItem on mobile.
 * Always render messages in a plain block below the control.
 */
const FieldMessages = ({ errorText, hintText }) => (
  <>
    {errorText ? (
      <div role='alert' style={fieldMessageStyle(true)}>
        {errorText}
      </div>
    ) : null}
    {!errorText && hintText ? (
      <div style={fieldMessageStyle(false)}>{hintText}</div>
    ) : null}
  </>
);

FieldMessages.propTypes = {
  errorText: PropTypes.string,
  hintText: PropTypes.string,
};

const ProfileFieldInputMob = ({
  field,
  value,
  onChange,
  error,
  helperText,
}) => {
  const fieldType = getEffectiveProfileFieldType(field);
  const isRequired = field?.is_checked === 1 || field?.is_checked === true;
  const label = getFieldLabel(field?.profile_field_name, isRequired);
  const itemClass = error ? 'ion-invalid ion-touched' : undefined;
  const errorText = helperText || undefined;
  const hintText =
    fieldType === PROFILE_FIELD_TYPES.DATE && !errorText
      ? 'Format: MM/DD/YYYY'
      : undefined;

  const messages = (
    <FieldMessages errorText={errorText} hintText={hintText} />
  );

  if (fieldType === PROFILE_FIELD_TYPES.TEXT_INPUT) {
    return (
      <div className='profile-field-input-mob'>
        <IonItem className={itemClass} lines='full'>
          <IonInput
            label={label}
            labelPlacement='stacked'
            value={value ?? ''}
            onIonInput={(e) => onChange(field, e.detail.value ?? '')}
          />
        </IonItem>
        {messages}
      </div>
    );
  }

  if (fieldType === PROFILE_FIELD_TYPES.DATE) {
    return (
      <div className='profile-field-input-mob'>
        <IonItem className={itemClass} lines='full'>
          <IonInput
            label={label}
            labelPlacement='stacked'
            value={value ?? ''}
            placeholder='MM/DD/YYYY'
            inputmode='numeric'
            maxlength={10}
            onIonInput={(e) => onChange(field, e.detail.value ?? '')}
          />
        </IonItem>
        {messages}
      </div>
    );
  }

  if (fieldType === PROFILE_FIELD_TYPES.MULTI_SELECT) {
    const optionNames = getProfileFieldOptionNames(field);
    const selectedValues = sanitizeMultiSelectValue(field, value);

    return (
      <div className='profile-field-input-mob'>
        <IonItem className={itemClass} lines='full'>
          <IonLabel position='stacked'>{label}</IonLabel>
          <IonSelect
            multiple
            value={selectedValues}
            interface='action-sheet'
            onIonChange={(e) => onChange(field, e.detail.value ?? [])}
          >
            {optionNames.map((name) => (
              <IonSelectOption key={name} value={name}>
                {name}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
        {messages}
      </div>
    );
  }

  const selectValue = sanitizeSingleSelectValue(field, value);

  return (
    <div className='profile-field-input-mob'>
      <IonItem className={itemClass} lines='full'>
        <IonLabel position='stacked'>{label}</IonLabel>
        <IonSelect
          value={selectValue}
          interface='action-sheet'
          onIonChange={(e) => onChange(field, e.detail.value)}
        >
          {dedupeProfileFieldOptions(field?.options).map((option, optionIndex) => {
            const optionName = option.option_name ?? option.name;
            return (
              <IonSelectOption
                key={`${option.option_id ?? optionName}-${optionIndex}`}
                value={optionName}
              >
                {optionName}
              </IonSelectOption>
            );
          })}
        </IonSelect>
      </IonItem>
      {messages}
    </div>
  );
};

ProfileFieldInputMob.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

export default ProfileFieldInputMob;
